import HttpClient from './http-client/http-client'

const baseProfileAPIURL = (version = '1') =>
  `${process.env.REACT_APP_PROFILE_URL}/api/v${version}/profile/`

class ProfileService {
  static instance
  client

  constructor(client) {
    if (!ProfileService.instance) {
      this.client = client
      ProfileService.instance = this
    }

    return ProfileService.instance
  }

  getProfileById(profileId) {
    return this.client.get(baseProfileAPIURL(), `GetById?profileId=${profileId}`, {
      name: 'getProfileById'
    })
  }

  hasProfiles() {
    return this.client.get(baseProfileAPIURL(), `HasProfiles`)
  }

  getProfileFilters(searchText = null, filters = null) {
    const data = {}
    if (searchText) data.searchText = searchText

    if (filters) {
      if (filters.tierFilter) data.tierFilter = filters.tierFilter
      if (filters.typeFilter) data.typeFilter = filters.typeFilter
      if (filters.categoryFilter) data.categoryFilter = filters.categoryFilter
      if (filters.statusFilter) data.statusFilter = filters.statusFilter
      if (filters.visibilityFilter) data.visibilityFilter = filters.visibilityFilter
      if (filters.destinationFilter) data.destinationFilter = filters.destinationFilter
      if (filters.countryFilter) data.countryFilter = filters.countryFilter
      if (filters.cityFilter) data.cityFilter = filters.cityFilter
      if (filters.accountFilter) data.accountFilter = filters.accountFilter
    }
    return this.client.post(baseProfileAPIURL(), `GetProfileFilters`, data)
  }

  getProfiles(
    rowsPerPage = null,
    currentPage = null,
    searchText = null,
    sortColumn = null,
    filters = null,
    sequence = 0
  ) {
    let params = ``
    if (rowsPerPage) params += `${params ? '&' : ''}rowsPerPage=${rowsPerPage}`
    if (currentPage) params += `${params ? '&' : ''}currentPage=${currentPage}`
    if (searchText) params += `${params ? '&' : ''}searchText=${encodeURIComponent(searchText)}`
    if (sortColumn) {
      params += `${params ? '&' : ''}sortColumnName=${sortColumn.sortColumnName}`
      params += `${params ? '&' : ''}sortDirection=${sortColumn.sortDirection}`
    }
    if (filters) {
      if (filters.tierFilter) params += `${params ? '&' : ''}tierFilter=${filters.tierFilter}`
      if (filters.typeFilter) params += `${params ? '&' : ''}typeFilter=${filters.typeFilter}`
      if (filters.categoryFilter)
        params += `${params ? '&' : ''}categoryFilter=${filters.categoryFilter}`

      if (filters.statusFilter) params += `${params ? '&' : ''}statusFilter=${filters.statusFilter}`
      if (filters.visibilityFilter)
        params += `${params ? '&' : ''}visibilityFilter=${filters.visibilityFilter}`

      if (filters.destinationFilter)
        params += `${params ? '&' : ''}destinationFilter=${filters.destinationFilter}`
      if (filters.countryFilter)
        params += `${params ? '&' : ''}countryFilter=${filters.countryFilter}`
      if (filters.cityFilter) params += `${params ? '&' : ''}cityFilter=${filters.cityFilter}`
      if (filters.accountFilter)
        params += `${params ? '&' : ''}accountFilter=${filters.accountFilter}`
    }
    params += `${params ? '&' : ''}sequence=${sequence}`

    return this.client.get(baseProfileAPIURL(), `GetProfileSummaries?${params}`)
  }

  getAssociatedItemsForDMOProfile(
    profilePin,
    searchText = null,
    filters = null,
    distance = null,
    destinationId = null,
    sortColumn = null
  ) {
    let params = ''

    if (profilePin) {
      params += `latitude=${profilePin.latitude}`
      params += `&longitude=${profilePin.longitude}`
    }

    if (distance) {
      params += `&distanceFrom=${distance}`
    }

    if (searchText) {
      params += `&searchText=${encodeURIComponent(searchText)}`
    }

    if (sortColumn) {
      params += `&sortColumnName=${sortColumn.sortColumnName}`
      params += `&sortDirection=${sortColumn.sortDirection}`
    }

    if (filters) {
      if (filters.categoryFilter)
        params +=
          params.length > 0
            ? `&categoryFilter=${filters.categoryFilter}`
            : `categoryFilter=${filters.categoryFilter}`
      if (filters.typeFilter)
        params +=
          params.length > 0
            ? `&typeFilter=${filters.typeFilter}`
            : `typeFilter=${filters.typeFilter}`
    }

    if (destinationId) {
      params += `&destinationId=${destinationId}`
    }

    return this.client.get(baseProfileAPIURL(), `GetProfiles?${params}`)
  }

  deleteProfile(profileId) {
    const payload = {
      profileId
    }

    return this.client.post(baseProfileAPIURL(), `Delete`, payload)
  }

  sendSuggestionEmail(emailSuggestionType, messageFields) {
    //messageFields on the API is a List<Tuple<string, string>>, so send eg. [{'Suggestion:', 'some text'}, {'Type:', 'Stays'}] where item1 is the first email body column and item2 is the second column.
    const payload = { emailSuggestionType, messageFields }
    return this.client.post(baseProfileAPIURL(), `SendSuggestionEmail`, payload)
  }

  multiProfileDelete(profileName) {
    const payload = {
      profileName
    }
    return this.client.post(baseProfileAPIURL(), `MultiDelete`, payload)
  }

  filterByCountries() {
    return this.client.get(baseProfileAPIURL(), `FilterByCountries`)
  }

  filterBySearch(type, categories, searchText, includePrivate, excludeIds) {
    const payload = {
      profileType: type,
      categories: categories,
      searchText: searchText,
      includePrivate: includePrivate,
      excludeIds: excludeIds
    }

    return this.client.post(baseProfileAPIURL(), 'FilterBySearch', payload)
  }

  getProfilesInReview() {
    return this.client.get(baseProfileAPIURL(), `GetProfilesInReview`)
  }

  publishProfile(profileId, isReviewRequired, profileSummaryImageBlobUrl, affiliate) {
    const payload = {
      profileId,
      profileSummaryImageBlobUrl,
      isReviewRequired,
      affiliate
    }
    return this.client.post(baseProfileAPIURL(), `Publish`, payload)
  }

  validateProfile(profile) {
    const payload = {
      profileId: profile.id,
      profileName: profile.name,
      isCreated: profile.isCreated,
      pin: profile.pin,
      type: profile.type
    }

    return this.client.post(baseProfileAPIURL(), `Validate`, payload)
  }

  createProfile(profile) {
    return this.client.post(baseProfileAPIURL(), `Create`, profile)
  }

  getMTStatus(profileId) {
    return this.client.get(baseProfileAPIURL(), `GetMtStatus?profileId=${profileId}`)
  }

  checkExistsInCache(profileId) {
    return this.client.get(baseProfileAPIURL(), `CheckExistsInCache?profileId=${profileId}`)
  }

  updateProfile(profile) {
    return this.client.post(baseProfileAPIURL(), `Update`, profile)
  }

  enableDisableProfile(requestPayload) {
    return this.client.post(baseProfileAPIURL(), `EnableDisableProfile`, requestPayload)
  }

  getAllFeatures() {
    return this.client.get(baseProfileAPIURL(), `GetAllFeatures`)
  }

  upgradeOrDowngradeProfile(requestPayload) {
    return this.client.post(baseProfileAPIURL(), `UpgradeOrDowngradeProfile`, requestPayload)
  }

  cancelUpgradeRequest(requestPayload) {
    return this.client.post(baseProfileAPIURL(), `CancelUpgradeRequest`, requestPayload)
  }

  sendWetuShareEmail(payload) {
    return this.client.post(baseProfileAPIURL(), `SendWetuShareEmail`, payload)
  }

  sendShareProfileEmail(profileId, payload) {
    return this.client.post(baseProfileAPIURL('3'), `${profileId}/share/email`, payload)
  }

  getSuggestedDestinations(latitude, longitude, includePrivate, categories) {
    const params = new URLSearchParams({ latitude, longitude, includePrivate, categories })
    return this.client.get(baseProfileAPIURL(), `GetSuggestedDestinations?${params}`)
  }

  setProfileDestinations(associatedItems) {
    return this.client.post(baseProfileAPIURL(), `SetSelectedDestinations`, { associatedItems })
  }

  setProfilePreferredDestination(profileId, preferredDestinationId, campSiteId) {
    const params = new URLSearchParams({ profileId, preferredDestinationId })

    if (campSiteId) {
      params.append('campSiteId', campSiteId)
    }

    return this.client.put(baseProfileAPIURL(), `SetPreferredDestination?${params}`)
  }

  updateStatusNotification(profileId, statusNotification) {
    const payload = {
      profileId,
      statusNotification
    }
    return this.client.post(baseProfileAPIURL(), `UpdateStatusNotification`, payload)
  }

  addOrRemoveProfileOperator(profileId, profileOperatorsToCreate, profileOperatorsToDelete) {
    const payload = {
      profileId,
      profileOperatorsToCreate,
      profileOperatorsToDelete
    }
    return this.client.post(baseProfileAPIURL(), `AddOrRemoveProfileOperator`, payload)
  }

  setProfileNameOnLanding(profileId, showProfileName) {
    const payload = {
      profileId,
      showProfileName
    }
    return this.client.post(baseProfileAPIURL(), `SetProfileNameOnLanding`, payload)
  }

  getCountryInformation(iso2CountryCode) {
    const params = new URLSearchParams({ iso2CountryCode })
    return this.client.get(baseProfileAPIURL(), `GetCountryInformation?${params}`)
  }

  getProfilesWithPendingUpgradeRequest(profileId, isProfileLoaded) {
    const params = profileId
      ? new URLSearchParams({ profileId, isProfileLoaded })
      : new URLSearchParams({ isProfileLoaded })
    return this.client.get(baseProfileAPIURL(), `GetProfilesWithPendingUpgradeRequest?${params}`)
  }

  getAutomationReviewById(id) {
    return this.client.get(
      process.env.REACT_APP_PROFILE_URL,
      `api/v2/Profile/automation-review/${id}`
    )
  }

  // v2
  partialSaveSalesGuides(profileId, salesGuides) {
    const payload = {
      profileId,
      salesGuides
    }
    return this.client.post(baseProfileAPIURL('2'), `${profileId}/salesguides/save`, payload)
  }

  createProfileFromUrl(createProfileAutomationRequest) {
    return this.client.post(
      process.env.REACT_APP_PROFILE_URL,
      `api/v2/Profile/create/url`,
      createProfileAutomationRequest
    )
  }

  completeFromAutomationReview(reviewId, profileId) {
    return this.client.post(
      process.env.REACT_APP_PROFILE_URL,
      `api/v2/Profile/complete/automationReview/${reviewId}/profile/${profileId}`,
      {}
    )
  }

  validateUrl(url) {
    return this.client.post(process.env.REACT_APP_PROFILE_URL, `api/v2/Profile/validate/url`, url)
  }

  getProfileTags(profileId) {
    return this.client.get(baseProfileAPIURL('2'), `${profileId}/GetProfileTags`)
  }

  addProfileTag(profileId, tagId) {
    return this.client.put(baseProfileAPIURL('2'), `${profileId}/AddTag/${tagId}`)
  }

  removeProfileTag(profileId, tagId) {
    return this.client.delete(baseProfileAPIURL('2'), `${profileId}/DeleteTag/${tagId}`)
  }

  /**
   * Upload profile icon to the API
   * @param {number} profileId - The profile ID
   * @param {File} file - The image file to upload
   * @param {Function} setProgress - Callback to track upload progress (0-100)
   * @param {AbortController} abortController - Controller to abort the upload
   * @returns {Promise<Object>} Response data with iconUrl and message
   */
  async uploadProfileIcon(profileId, file, setProgress, abortController) {
    const formData = new FormData()
    formData.append('file', file)

    const signal = abortController?.signal

    const baseUrl = baseProfileAPIURL(2)
    const url = `${profileId}/icon/upload`

    const response = await this.client.post(baseUrl, url, formData, {}, {
      onUploadProgress: (progressEvent) => {
        if (setProgress && progressEvent.total > 0) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
          setProgress(progress)
        }
      },
      signal,
      timeout: 0 // No timeout for large files
    })

    return response
  }

  /**
   * Remove the profile icon
   * @param {number} profileId - The profile ID
   * @returns {Promise<Object>} Response data with message
   */
  removeProfileIcon(profileId) {
    const baseUrl = baseProfileAPIURL(2)
    const url = `/${profileId}/icon`

    return this.client.delete(baseUrl, url)
  }
}

const instance = new ProfileService(HttpClient)
export default instance
